import { useState, useRef, useEffect } from "react";
import { getCurrentLanguage, type SupportedLanguage } from "@/config/domains";
import DE from "country-flag-icons/react/3x2/DE";
import PL from "country-flag-icons/react/3x2/PL";
import UA from "country-flag-icons/react/3x2/UA";
import CZ from "country-flag-icons/react/3x2/CZ";
import styles from "./LanguageSwitcher.module.scss";

interface LanguageOption {
  code: SupportedLanguage;
  label: string;
  domain: string;
  Flag: React.ComponentType<{ className?: string }>;
}

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: "de", label: "DE", domain: "hixa.eu", Flag: DE },
  { code: "pl", label: "PL", domain: "poland.hixa.eu", Flag: PL },
  { code: "ua", label: "UA", domain: "ukraine.hixa.eu", Flag: UA },
  { code: "cz", label: "CZ", domain: "czechia.hixa.eu", Flag: CZ },
];

const LanguageSwitcher = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentLang = getCurrentLanguage();

  // Закриття dropdown при кліку поза ним
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageSwitch = (targetLang: SupportedLanguage) => {
    if (targetLang === currentLang) {
      setIsOpen(false);
      return;
    }

    const targetOption = LANGUAGE_OPTIONS.find(
      (opt) => opt.code === targetLang
    );
    if (!targetOption) return;

    // Отримуємо поточний шлях та параметри
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const currentHash = window.location.hash;

    // Визначаємо цільовий домен
    let targetDomain = targetOption.domain;

    // Для localhost використовуємо порти
    if (window.location.hostname === "localhost") {
      const portMap: Record<SupportedLanguage, string> = {
        de: "5173",
        ua: "5174",
        pl: "5175",
        cz: "5176",
      };
      targetDomain = `localhost:${portMap[targetLang]}`;
    }

    // Формуємо URL з збереженням шляху
    const protocol = window.location.protocol;
    const newUrl = `${protocol}//${targetDomain}${currentPath}${currentSearch}${currentHash}`;

    // Перенаправляємо
    window.location.href = newUrl;
  };

  const currentOption = LANGUAGE_OPTIONS.find(
    (opt) => opt.code === currentLang
  );

  return (
    <div className={styles.languageSwitcher} ref={dropdownRef}>
      <button
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Вибрати мову"
        aria-expanded={isOpen}
      >
        {currentOption?.Flag && <currentOption.Flag className={styles.flag} />}
        <span className={`${styles.label} ${styles.triggerLabel}`}>{currentOption?.label}</span>
        <svg
          className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ""}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M1.50002 4L6.00002 8L10.5 4"
            strokeWidth="1.5"
            stroke="currentColor"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          {LANGUAGE_OPTIONS.map((option) => (
            <button
              key={option.code}
              className={`${styles.option} ${
                option.code === currentLang ? styles.active : ""
              }`}
              onClick={() => handleLanguageSwitch(option.code)}
              disabled={option.code === currentLang}
            >
              <option.Flag className={styles.flag} />
              <span className={styles.label}>{option.label}</span>
              {option.code === currentLang && (
                <svg
                  className={styles.checkmark}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M13.5 4L6 11.5L2.5 8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
